import { useRef } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartDownloadButton } from "@/components/common/ChartDownloadButton";
import { EmptyState } from "@/components/common/ui-bits";
import { compact } from "@/components/dashboard/MonthlyChart";
import { PALETTE } from "@/components/dashboard/Breakdowns";
import { money, monthShort } from "@/lib/format";

const ChartCard = ({ title, chartRef, filename, testId, children }) => (
  <div ref={chartRef} className="surface p-5 sm:p-6 h-full fade-up" data-testid={testId}>
    <div className="flex items-center justify-between gap-2 mb-4">
      <h3 className="font-heading text-lg font-semibold">{title}</h3>
      <ChartDownloadButton targetRef={chartRef} filename={filename} testId={`${testId}-download-png`} />
    </div>
    {children}
  </div>
);

const moneyTip = (v) => money(v);

export function ReportIncomeChart({ rows, period, empty }) {
  const ref = useRef(null);
  return (
    <ChartCard title="Income vs expenses" chartRef={ref} testId="report-income-chart"
      filename={`DaLilShop_Report_IncomeVsExpenses_${period.start}_to_${period.end}.png`}>
      {empty ? <EmptyState text="No data for this period" testId="report-income-chart-empty" /> : (
        <div className="h-[280px] -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="hsl(200 14% 90%)" />
              <XAxis dataKey="month" tickFormatter={monthShort} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={52} />
              <Tooltip formatter={moneyTip} labelFormatter={monthShort} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Bar dataKey="income_cents" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
              <Bar dataKey="expense_cents" name="Expenses" fill="#F43F5E" radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
              <Line dataKey="net_cents" name="Net" stroke="#111111" strokeWidth={2.5} dot={{ r: 3, fill: "#FFC72C", stroke: "#111111" }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}

export function ReportCategoryChart({ lines, period }) {
  const ref = useRef(null);
  const data = lines.slice(0, 10);
  return (
    <ChartCard title="Expenses by category" chartRef={ref} testId="report-category-chart"
      filename={`DaLilShop_Report_ExpensesByCategory_${period.start}_to_${period.end}.png`}>
      {data.length === 0 ? <EmptyState text="No data for this period" testId="report-category-chart-empty" /> : (
        <div style={{ height: Math.max(180, data.length * 40 + 30) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke="hsl(200 14% 90%)" />
              <XAxis type="number" tickFormatter={compact} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                tickFormatter={(n) => (n.length > 22 ? `${n.slice(0, 21)}…` : n)} />
              <Tooltip formatter={moneyTip} />
              <Bar dataKey="total_cents" name="Spent" radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false}>
                {data.map((d, i) => <Cell key={d.category_id} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}
